import React, {useCallback, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {BoosterOrb} from './BoosterOrb';
import {BoosterOmnibar} from './BoosterOmnibar';
import {BoosterOrbCarousel, formatFactCount} from './feed/BoosterOrbCarousel';
import {useMemoryIngest} from './useMemoryIngest';
import {usePreventFileDropNavigation} from '../../../hooks/usePreventFileDropNavigation';
import {
    BOOSTER_STEP_MEMORY,
    BOOSTER_STEP_REVIEW,
    BOOSTER_STEP_USE_IA,
    boosterPathFor,
} from './boosterSteps';

/**
 * El organismo del cliente: el orbe, sus contadores y las dos formas de alimentarlo.
 *
 * Vive acá y no dentro del escenario porque lo usan las dos generaciones del Recorrido — el
 * escenario por-ruta (`BoosterStage`, paso Alimenta) y la one-page (`BoosterFlowContent`, acto
 * Alimenta). Es el mismo objeto en los dos lados y no puede tener dos implementaciones.
 *
 * El conteo lo recibe por prop: quien pide el brief es el contenedor, que es el único que sabe si
 * alguien más en la página ya lo pidió. `factCount === null` es "todavía no llegó" y `briefFailed`
 * es "no lo pude leer" — los dos terminan sin número, pero no son lo mismo y no se dibujan igual.
 *
 * `facts` sigue la misma regla: son los hechos del brief que YA pidió el contenedor, y sirven para
 * la nube del carrusel. Es opcional — sin ellos el carrusel simplemente no ofrece esa vista.
 *
 * **Sobre la ingesta.** Adentro de este componente hay UNA sola —la comparten la Barra Total y el
 * drop sobre el orbe, que son la misma acción con dos gestos— y se puede recibir por prop, igual
 * que hace `BoosterOmnibar`. Desde el 08/09 esa instancia es la ÚNICA de la pantalla: el acto
 * Alimenta perdió la suya cuando se retiró el compositor plegado (card ScPxmmB3), así que las dos
 * puertas de carga viven acá y comparten un solo `sending`. La ventana que este docblock advertía
 * —dos instancias, dos `sending`, soltar sobre el orbe mientras la otra mandaba— **ya no existe**.
 *
 * Si algún día un contenedor baja la ingesta por prop, queda una salvedad: la animación de
 * absorción la dispara el callback LOCAL del hook, así que ese contenedor tiene que disparar la
 * absorción por su cuenta.
 */
export function BoosterFeedHero({
    clientId,
    clientName,
    factCount = null,
    // ¿El listado del que sale `factCount` vino recortado? Es lo que decide el `+`. Sin esto se
    // adivinaba comparando contra el tope del brief (300), que dejó de ser la fuente del número.
    factCountTruncated,
    briefFailed = false,
    pendingCount = 0,
    hasIa = false,
    // Estado de la conexión con la IA: `true`/`false` cuando el contenedor lo sabe, `null` cuando
    // no (el escenario por-ruta no lo pide). Con `null` el chip no afirma nada: sólo lleva a la
    // banda, como siempre.
    iaConnected = null,
    // El inventario de la cuenta (`useClientPromptLibrary`): `{available, missing}`. Sin él, o sin
    // `onOpenAccount`, el chip «La cuenta» no se dibuja — un chip que no puede decir qué falta ni
    // abrir nada es ruido.
    account = null,
    onOpenIa,
    onOpenAccount,
    onIngested,
    facts = null,
    // La ingesta del acto, si el contenedor ya la tiene. El hook local es el fallback: nunca corren
    // los dos, y `useMemoryIngest` no tiene efectos, así que la instancia que no se usa no cuesta.
    ingest,
    // El tipo declarado de la Barra Total y su control. Pasan de largo hasta `BoosterOmnibar`: los
    // sostiene el contenedor del acto porque quien ELIGE el tipo es el instructivo, que vive en un
    // componente hermano de éste. Sin `onKindChange` (el escenario por-ruta, que no monta el
    // instructivo) la barra no dibuja el selector.
    kind,
    onKindChange,
    omnibarFocusToken = 0,
}) {
    const {t} = useTranslation();
    const [absorbToken, setAbsorbToken] = useState(0);
    const [dragging, setDragging] = useState(false);
    // `dragleave` dispara al cruzar CADA hijo del orbe (el canvas, la etiqueta, el carrusel): sin
    // contar la profundidad, el resaltado parpadea mientras el usuario mueve el mouse adentro.
    const dragDepthRef = useRef(0);
    // Soltar el archivo 2px afuera del orbe navegaría al archivo y se llevaría la página entera.
    usePreventFileDropNavigation();
    const countLoading = factCount === null && !briefFailed;
    // Mockup: «Tu IA · conectada» y «La cuenta · N por conectar» — dos chips que abren la
    // configuración sin salir de acá. El LED dice el estado; el click lo despliega.
    const iaLed = iaConnected === null ? '-ia' : (iaConnected ? '-ok' : '-warn');
    const iaKey = iaConnected === null
        ? 'ia_booster.hud.your_ia'
        : (iaConnected ? 'ia_booster.hud.ia_connected' : 'ia_booster.hud.ia_disconnected');
    const accountKnown = Boolean(account) && Array.isArray(account.available) && Boolean(onOpenAccount);
    const missingCount = accountKnown ? (account.missing || []).length : 0;

    // Material recién cargado: el orbe lo absorbe. El NÚMERO no se toca — el conteo son hechos
    // aprobados y la extracción es asíncrona, así que todavía no hay ninguno de este material. Lo
    // que la animación comunica es que ENTRÓ algo, no cuánto sabe la memoria ahora. Y se avisa
    // hacia afuera: la lista de material está en la misma página, y sin el aviso quedaba vieja.
    const handleIngested = useCallback((doc) => {
        setAbsorbToken(token => token + 1);
        if (onIngested) onIngested(doc);
    }, [onIngested]);

    // La ingesta la comparten los dos gestos: escribir en la Barra Total y soltar sobre el orbe.
    const own = useMemoryIngest(clientId, handleIngested);
    const active = ingest || own;
    const {ingestDrop, sending} = active;

    const onDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = 0;
        setDragging(false);
        // Mismo gate que la Barra Total, que deshabilita textarea y botón mientras sube: sin él,
        // dos drops seguidos sobre el orbe crean dos documentos. Y se DICE: un drop que no hace
        // nada en silencio se lee como «no anda».
        if (sending) {
            toast.error(t('ia_booster.omni.busy'));
            return;
        }
        ingestDrop(e.dataTransfer);
    }, [ingestDrop, sending, t]);

    return (
        <div className="iab-hero">
            {/* El orbe ES una zona de drop: soltar el material sobre el organismo del cliente es el
                gesto del mockup, y la Barra Total es su versión con teclado. Las dos terminan en el
                mismo `ingest`. */}
            <div
                className={`iab-orb ${dragging ? '-dragging' : ''} ${countLoading ? '-loading' : ''}`}
                onDragEnter={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    dragDepthRef.current += 1;
                    setDragging(true);
                }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDragLeave={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    dragDepthRef.current -= 1;
                    if (dragDepthRef.current <= 0) { dragDepthRef.current = 0; setDragging(false); }
                }}
                onDrop={onDrop}
            >
                <BoosterOrb count={factCount || 0} absorbToken={absorbToken}/>
                <span className="iab-orb-droplabel">{t('ia_booster.orb.drop')}</span>
                {/* El orbe sigue siendo `<canvas>` puro. Lo que gira a su alrededor son TRES vistas
                    —el conteo, la nube de hechos y el instructivo— y viven afuera del dibujo: meter
                    el carrusel adentro de `BoosterOrb` mezclaría el bucle de rAF con estado de React
                    y obligaría a redibujar el canvas en cada cambio de vista. */}
                <BoosterOrbCarousel
                    clientName={clientName}
                    factCount={factCount}
                    factCountTruncated={factCountTruncated}
                    briefFailed={briefFailed}
                    facts={facts}
                />
            </div>

            <div className="iab-hud">
                {pendingCount > 0 && (
                    <Link className="iab-chip" to={boosterPathFor(BOOSTER_STEP_REVIEW, clientId)}>
                        <span className="iab-chip-led -alert"/>
                        <b>{pendingCount}</b>&nbsp;{t('ia_booster.hud.pending')}
                    </Link>
                )}
                {factCount !== null && (
                    <Link className="iab-chip" to={boosterPathFor(BOOSTER_STEP_MEMORY, clientId)}>
                        <span className="iab-chip-led -ia"/>
                        {/* El MISMO formato que el orbe, que está a centímetros: el tope del brief
                            aplicado en un solo lado dejaba «300+» arriba y «300» acá. */}
                        <b>{formatFactCount(factCount, factCountTruncated)}</b>&nbsp;{t('ia_booster.hud.approved')}
                    </Link>
                )}
                {hasIa && (onOpenIa ? (
                    <button type="button" className="iab-chip" onClick={onOpenIa}>
                        <span className={`iab-chip-led ${iaLed}`}/>
                        {t(iaKey)}
                    </button>
                ) : (
                    <Link className="iab-chip" to={boosterPathFor(BOOSTER_STEP_USE_IA, clientId)}>
                        <span className="iab-chip-led -ok"/>
                        {t('ia_booster.hud.your_ia')}
                    </Link>
                ))}
                {accountKnown && (
                    <button type="button" className="iab-chip -account" onClick={onOpenAccount}>
                        <span className={`iab-chip-led ${missingCount > 0 ? '-warn' : '-ok'}`}/>
                        {t('ia_booster.hud.account')}
                        {' · '}
                        {missingCount > 0 ? (
                            <>
                                <b>{missingCount}</b>&nbsp;{t('ia_booster.hud.account_missing')}
                            </>
                        ) : t('ia_booster.hud.account_ok')}
                    </button>
                )}
            </div>

            <BoosterOmnibar clientId={clientId} ingest={active}
                            kind={kind} onKindChange={onKindChange}
                            focusToken={omnibarFocusToken}/>
        </div>
    );
}

export default BoosterFeedHero;
